import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  forwardRef,
} from "react";
import {
  GripVertical,
  MoreHorizontal,
  ArrowUp,
  ArrowDown,
  ArrowRightLeft,
  MessageSquare,
  Plus,
  Trash2,
  Package,
  Users,
  Minus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type {
  BaseLineItemEntity,
  ZeroLineItemEntity,
} from "@/types/finance-entities";
import { calculateLineItemTotal } from "@/lib/finance-calculations";
import {
  QuoteLineItemTemplate,
  resolveTemplateName,
} from "@/hooks/useQuoteLineItemTemplates";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  getAutoFillFactor,
  type LineItemType,
} from "@/lib/equipment-factor-scale";

interface OtherGroup {
  id: string;
  name: string;
}

function formatAmount(amount: number): string {
  return amount.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function useDraftField<T>(serverValue: T) {
  const [draft, setDraft] = useState<T | null>(null);

  useEffect(() => {
    if (draft !== null && Object.is(draft, serverValue)) {
      setDraft(null);
    }
  }, [draft, serverValue]);

  const commit = useCallback(() => {
    if (draft === null) return undefined;
    if (Object.is(draft, serverValue)) {
      setDraft(null);
      return undefined;
    }
    return draft;
  }, [draft, serverValue]);

  return {
    value: draft ?? serverValue,
    setValue: setDraft,
    commit,
  };
}

export interface FinanceLineItemRowProps {
  item: BaseLineItemEntity | ZeroLineItemEntity;
  onUpdate: (
    updates: Partial<{
      name: string;
      amount: number;
      factor: number;
      unit_price: number;
      unitPrice: number;
      discount: number;
      notes: string;
      line_item_type: LineItemType;
      lineItemType: LineItemType;
      external_note: string;
      externalNote: string;
    }>,
  ) => void;
  onDelete: () => void;
  onNextItem: () => void;
  onNewGroup: () => void;
  autoFocus?: boolean;
  templates?: QuoteLineItemTemplate[];
  projectLanguage?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  // DnD props (desktop only)
  dragHandleProps?: Record<string, unknown>;
  style?: React.CSSProperties;
  // Move actions
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onMoveToGroup?: (targetGroupId: string) => void;
  otherGroups?: OtherGroup[];
  isFirst?: boolean;
  isLast?: boolean;
  // Insert actions
  onAddItemBefore?: () => void;
  onAddTitleBefore?: () => void;
  readonly?: boolean;
  fieldNaming?: "legacy" | "zero";
}

export const FinanceLineItemRow = forwardRef<
  HTMLTableRowElement,
  FinanceLineItemRowProps
>(function FinanceLineItemRow(
  {
    item,
    onUpdate,
    onDelete,
    onNextItem,
    onNewGroup,
    autoFocus,
    templates = [],
    projectLanguage = "de",
    eventStartDate,
    eventEndDate,
    dragHandleProps,
    style,
    onMoveUp,
    onMoveDown,
    onMoveToGroup,
    otherGroups = [],
    isFirst,
    isLast,
    onAddItemBefore,
    onAddTitleBefore,
    readonly,
    fieldNaming = "legacy",
  },
  ref,
) {
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [notes, setNotes] = useState(item.notes);
  const isMobile = useIsMobile();
  const externalNoteValue =
    "externalNote" in item ? item.externalNote : item.external_note;
  const unitPriceValue = "unitPrice" in item ? item.unitPrice : item.unit_price;
  const lineItemTypeValue =
    "lineItemType" in item ? item.lineItemType : item.line_item_type;
  const isTitleValue = "isTitle" in item ? item.isTitle : item.is_title;

  const nameField = useDraftField(item.name);
  const externalNoteField = useDraftField(externalNoteValue);
  const amountField = useDraftField(item.amount);
  const factorField = useDraftField(item.factor);
  const unitPriceField = useDraftField(unitPriceValue);
  const discountField = useDraftField(item.discount);
  const lineItemTypeField = useDraftField<LineItemType>(
    lineItemTypeValue ?? "generic",
  );
  const name = nameField.value;
  const externalNote = externalNoteField.value;
  const amount = amountField.value;
  const factor = factorField.value;
  const unitPrice = unitPriceField.value;
  const discount = discountField.value;
  const lineItemType = lineItemTypeField.value;
  const [showExternalNote, setShowExternalNote] = useState(!!externalNoteValue);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const selectingRef = useRef(false);

  useEffect(() => {
    if (autoFocus && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [autoFocus]);

  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    setNotes(item.notes);
  }, [item.notes]);
  useEffect(() => {
    if (externalNoteValue) setShowExternalNote(true);
  }, [externalNoteValue]);

  const commitName = useCallback(() => {
    const next = nameField.commit();
    if (next !== undefined) onUpdateRef.current({ name: next });
  }, [nameField]);
  const commitExternalNote = useCallback(() => {
    const next = externalNoteField.commit();
    if (next !== undefined) {
      onUpdateRef.current(
        fieldNaming === "zero"
          ? { externalNote: next }
          : { external_note: next },
      );
    }
  }, [externalNoteField, fieldNaming]);
  const commitAmount = useCallback(() => {
    const next = amountField.commit();
    if (next !== undefined) onUpdateRef.current({ amount: next });
  }, [amountField]);
  const commitFactor = useCallback(() => {
    const next = factorField.commit();
    if (next !== undefined) onUpdateRef.current({ factor: next });
  }, [factorField]);
  const commitUnitPrice = useCallback(() => {
    const next = unitPriceField.commit();
    if (next !== undefined) {
      onUpdateRef.current(
        fieldNaming === "zero" ? { unitPrice: next } : { unit_price: next },
      );
    }
  }, [fieldNaming, unitPriceField]);
  const commitDiscount = useCallback(() => {
    const next = discountField.commit();
    if (next !== undefined) onUpdateRef.current({ discount: next });
  }, [discountField]);

  const commitDrafts = useCallback(() => {
    const updates: Parameters<typeof onUpdateRef.current>[0] = {};
    const nextName = nameField.commit();
    const nextExternalNote = externalNoteField.commit();
    const nextAmount = amountField.commit();
    const nextFactor = factorField.commit();
    const nextUnitPrice = unitPriceField.commit();
    const nextDiscount = discountField.commit();
    const nextLineItemType = lineItemTypeField.commit();
    if (nextName !== undefined) updates.name = nextName;
    if (nextExternalNote !== undefined) {
      if (fieldNaming === "zero") updates.externalNote = nextExternalNote;
      else updates.external_note = nextExternalNote;
    }
    if (nextAmount !== undefined) updates.amount = nextAmount;
    if (nextFactor !== undefined) updates.factor = nextFactor;
    if (nextUnitPrice !== undefined) {
      if (fieldNaming === "zero") updates.unitPrice = nextUnitPrice;
      else updates.unit_price = nextUnitPrice;
    }
    if (nextDiscount !== undefined) updates.discount = nextDiscount;
    if (nextLineItemType !== undefined) {
      if (fieldNaming === "zero") updates.lineItemType = nextLineItemType;
      else updates.line_item_type = nextLineItemType;
    }
    if (Object.keys(updates).length === 0) return;
    onUpdateRef.current(updates);
  }, [
    amountField,
    discountField,
    externalNoteField,
    factorField,
    fieldNaming,
    lineItemTypeField,
    nameField,
    unitPriceField,
  ]);

  const updateFromTemplate = useCallback(
    ({
      nextName,
      nextAmount,
      nextFactor,
      nextType,
      nextUnitPrice,
    }: {
      nextName: string;
      nextAmount: number;
      nextFactor: number;
      nextType: LineItemType;
      nextUnitPrice: number;
    }) => {
      nameField.setValue(nextName);
      amountField.setValue(nextAmount);
      factorField.setValue(nextFactor);
      lineItemTypeField.setValue(nextType);
      unitPriceField.setValue(nextUnitPrice);
      setShowSuggestions(false);
      onUpdateRef.current({
        name: nextName,
        amount: nextAmount,
        factor: nextFactor,
        ...(fieldNaming === "zero"
          ? { unitPrice: nextUnitPrice, lineItemType: nextType }
          : { unit_price: nextUnitPrice, line_item_type: nextType }),
      });
    },
    [
      amountField,
      factorField,
      fieldNaming,
      lineItemTypeField,
      nameField,
      unitPriceField,
    ],
  );

  const onNextItemRef = useRef(onNextItem);
  onNextItemRef.current = onNextItem;
  const onNewGroupRef = useRef(onNewGroup);
  onNewGroupRef.current = onNewGroup;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowSuggestions(false);
        return;
      }
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && e.shiftKey) {
        e.preventDefault();
        setShowSuggestions(false);
        commitDrafts();
        onNewGroupRef.current();
      } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowSuggestions(false);
        commitDrafts();
        onNextItemRef.current();
      } else if (e.key === "Enter") {
        setShowSuggestions(false);
        commitDrafts();
      }
    },
    [commitDrafts],
  );

  // Filter templates by name match (searches translated name and code), preferring same line_item_type
  const filteredTemplates = useMemo(() => {
    if (!name.trim()) {
      // Show all templates when field is empty (e.g., on focus)
      return templates;
    }
    if (templates.length === 0) return [];
    const query = name.toLowerCase();
    const nameMatches = templates.filter((t) => {
      const resolved = resolveTemplateName(
        t.name,
        projectLanguage,
      ).toLowerCase();
      const code = (t.code || "").toLowerCase();
      const matchesTranslated = resolved.includes(query);
      const matchesCode = code.includes(query);
      const isExactMatch = resolved === query;
      return (matchesTranslated || matchesCode) && !isExactMatch;
    });
    const sameType = nameMatches.filter(
      (t) => t.line_item_type === lineItemType,
    );
    return sameType.length > 0 ? sameType : nameMatches;
  }, [name, templates, projectLanguage, lineItemType]);

  const isPopoverOpen = showSuggestions && filteredTemplates.length > 0;

  const handleSelectTemplate = useCallback(
    (template: QuoteLineItemTemplate) => {
      const resolvedName = resolveTemplateName(template.name, projectLanguage);
      const newType = template.line_item_type;
      const autoFactor =
        getAutoFillFactor(newType, eventStartDate, eventEndDate) ?? 1;
      updateFromTemplate({
        nextName: resolvedName,
        nextAmount: template.default_amount,
        nextFactor: autoFactor,
        nextType: newType,
        nextUnitPrice: template.default_unit_price,
      });
      selectingRef.current = false;
    },
    [projectLanguage, eventStartDate, eventEndDate, updateFromTemplate],
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      nameField.setValue(value);
      setShowSuggestions(true);
    },
    [nameField],
  );

  const handleNameBlur = useCallback(() => {
    if (selectingRef.current) return;
    setShowSuggestions(false);
    commitName();
  }, [commitName]);

  const handleNameFocus = useCallback(() => {
    // Always show suggestions on focus to display all available templates
    setShowSuggestions(true);
  }, []);

  const handleTypeChange = useCallback(
    (newType: LineItemType) => {
      const autoFactor =
        getAutoFillFactor(newType, eventStartDate, eventEndDate) ?? 1;
      lineItemTypeField.setValue(newType);
      factorField.setValue(autoFactor);
      onUpdateRef.current({
        ...(fieldNaming === "zero"
          ? { lineItemType: newType }
          : { line_item_type: newType }),
        factor: autoFactor,
      });
    },
    [eventStartDate, eventEndDate, factorField, fieldNaming, lineItemTypeField],
  );

  const total = calculateLineItemTotal(amount, factor, unitPrice, discount);

  // Title row: simplified rendering (name only, no financial fields)
  if (isTitleValue) {
    const saveName = () => {
      commitName();
    };

    const handleTitleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && e.shiftKey) {
        e.preventDefault();
        saveName();
        onNewGroupRef.current();
      } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        saveName();
        onNextItemRef.current();
      } else if (e.key === "Enter") {
        saveName();
      }
    };

    const handleTitleBlur = () => {
      saveName();
    };

    const titleActionMenu = readonly ? null : (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <MoreHorizontal className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {onMoveUp && (
            <DropdownMenuItem onClick={onMoveUp} disabled={isFirst}>
              <ArrowUp className="h-4 w-4 mr-2" />
              Move up
            </DropdownMenuItem>
          )}
          {onMoveDown && (
            <DropdownMenuItem onClick={onMoveDown} disabled={isLast}>
              <ArrowDown className="h-4 w-4 mr-2" />
              Move down
            </DropdownMenuItem>
          )}
          {onMoveToGroup && otherGroups.length > 0 && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <ArrowRightLeft className="h-4 w-4 mr-2" />
                Move to group
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {otherGroups.map((g) => (
                  <DropdownMenuItem
                    key={g.id}
                    onClick={() => onMoveToGroup!(g.id)}
                  >
                    {g.name || "Unnamed group"}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}
          {(onAddItemBefore || onAddTitleBefore) && <DropdownMenuSeparator />}
          {onAddItemBefore && (
            <DropdownMenuItem onClick={onAddItemBefore}>
              <Plus className="h-4 w-4 mr-2" />
              Add line item before
            </DropdownMenuItem>
          )}
          {onAddTitleBefore && (
            <DropdownMenuItem onClick={onAddTitleBefore}>
              <Plus className="h-4 w-4 mr-2" />
              Add title before
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-destructive" onClick={onDelete}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );

    if (isMobile) {
      return (
        <div className="border-b py-3">
          <div className="flex items-center gap-2">
            <Input
              ref={nameInputRef}
              data-item-name={item.id}
              value={name}
              onChange={(e) => nameField.setValue(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
              placeholder="Section title"
              className="h-8 flex-1 font-semibold"
              autoComplete="off"
              disabled={readonly}
            />
            {titleActionMenu}
          </div>
        </div>
      );
    }

    return (
      <tr ref={ref} style={style} className="border-b last:border-0 group">
        <td className="py-2 pr-1 w-8">
          {dragHandleProps && !readonly ? (
            <GripVertical
              data-testid={`line-item-drag-handle-${item.id}`}
              className="h-4 w-4 text-muted-foreground cursor-grab touch-none opacity-0 group-hover:opacity-100 transition-opacity"
              {...dragHandleProps}
            />
          ) : null}
        </td>
        <td colSpan={6} className="py-2 pr-2">
          <Input
            ref={nameInputRef}
            data-item-name={item.id}
            value={name}
            onChange={(e) => nameField.setValue(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            placeholder="Section title"
            className="h-8 font-semibold"
            autoComplete="off"
            disabled={readonly}
          />
        </td>
        <td className="py-2 pl-2">
          {titleActionMenu && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              {titleActionMenu}
            </div>
          )}
        </td>
      </tr>
    );
  }

  const numberInputProps = {
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select(),
    className:
      "h-8 text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]",
    disabled: readonly,
  };

  const handleSaveNotes = () => {
    onUpdate({ notes });
    setIsNotesOpen(false);
  };

  const typeIcon =
    lineItemType === "equipment" ? (
      <Package className="h-3.5 w-3.5" />
    ) : lineItemType === "crew" ? (
      <Users className="h-3.5 w-3.5" />
    ) : (
      <Minus className="h-3.5 w-3.5 text-muted-foreground" />
    );

  const typeDropdown = readonly ? (
    <div className="h-8 w-8 flex items-center justify-center flex-shrink-0">
      {typeIcon}
    </div>
  ) : (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 flex-shrink-0"
          title={lineItemType}
        >
          {typeIcon}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={() => handleTypeChange("generic")}>
          <Minus className="h-4 w-4 mr-2 text-muted-foreground" />
          Generic
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleTypeChange("equipment")}>
          <Package className="h-4 w-4 mr-2" />
          Equipment
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleTypeChange("crew")}>
          <Users className="h-4 w-4 mr-2" />
          Crew
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const nameInput = (
    <Input
      ref={nameInputRef}
      data-item-name={item.id}
      data-testid="line-item-name"
      value={name}
      onChange={handleNameChange}
      onBlur={handleNameBlur}
      onFocus={handleNameFocus}
      onKeyDown={handleKeyDown}
      placeholder="Item name"
      className={isMobile ? "h-8 flex-1" : "h-8"}
      autoComplete="off"
      disabled={readonly}
    />
  );

  const suggestionsPopover = (
    <Popover open={isPopoverOpen} onOpenChange={setShowSuggestions}>
      <PopoverAnchor asChild>{nameInput}</PopoverAnchor>
      <PopoverContent
        className="p-1 w-[--radix-popover-trigger-width]"
        align="start"
        side="bottom"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
      >
        {filteredTemplates.map((template) => {
          const translated = resolveTemplateName(
            template.name,
            projectLanguage,
          );
          const displayName = template.code
            ? `${template.code} ${translated}`
            : translated;
          return (
            <div
              key={template.id}
              onMouseDown={(e) => {
                e.preventDefault();
                selectingRef.current = true;
                handleSelectTemplate(template);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                selectingRef.current = true;
                handleSelectTemplate(template);
              }}
              className="flex justify-between items-center w-full rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground"
            >
              <span>{displayName}</span>
              <span className="text-xs text-muted-foreground tabular-nums ml-2">
                {formatAmount(template.default_unit_price)}
              </span>
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );

  const notesDialog = (
    <Dialog open={isNotesOpen} onOpenChange={setIsNotesOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Internal Notes</DialogTitle>
          <DialogDescription>
            Add internal notes for "{name || "this item"}" (not shown on offer)
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes..."
          rows={4}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsNotesOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveNotes}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const actionMenu = readonly ? null : (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7">
          <MoreHorizontal className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onMoveUp && (
          <DropdownMenuItem onClick={onMoveUp} disabled={isFirst}>
            <ArrowUp className="h-4 w-4 mr-2" />
            Move up
          </DropdownMenuItem>
        )}
        {onMoveDown && (
          <DropdownMenuItem onClick={onMoveDown} disabled={isLast}>
            <ArrowDown className="h-4 w-4 mr-2" />
            Move down
          </DropdownMenuItem>
        )}
        {onMoveToGroup && otherGroups.length > 0 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Move to group
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {otherGroups.map((g) => (
                <DropdownMenuItem
                  key={g.id}
                  onClick={() => onMoveToGroup(g.id)}
                >
                  {g.name || "Unnamed group"}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        {(onAddItemBefore || onAddTitleBefore) && <DropdownMenuSeparator />}
        {onAddItemBefore && (
          <DropdownMenuItem onClick={onAddItemBefore}>
            <Plus className="h-4 w-4 mr-2" />
            Add line item before
          </DropdownMenuItem>
        )}
        {onAddTitleBefore && (
          <DropdownMenuItem onClick={onAddTitleBefore}>
            <Plus className="h-4 w-4 mr-2" />
            Add title before
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setNotes(item.notes);
            setIsNotesOpen(true);
          }}
        >
          <MessageSquare
            className={`h-4 w-4 mr-2 ${item.notes ? "text-primary" : ""}`}
          />
          Edit internal notes
        </DropdownMenuItem>
        {!showExternalNote && (
          <DropdownMenuItem
            onClick={() => {
              setShowExternalNote(true);
              // Focus the external note input after a brief delay
              setTimeout(() => {
                const externalNoteInput =
                  document.querySelector<HTMLInputElement>(
                    `[data-external-note="${item.id}"]`,
                  );
                externalNoteInput?.focus();
              }, 100);
            }}
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Add external note
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-destructive" onClick={onDelete}>
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Mobile card layout
  if (isMobile) {
    return (
      <>
        <div className="border-b py-3 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 flex-1">
              {typeDropdown}
              {suggestionsPopover}
            </div>
            {actionMenu}
          </div>
          {showExternalNote && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                External note (shown on offer)
              </Label>
              <Input
                data-external-note={item.id}
                value={externalNote}
                onChange={(e) => externalNoteField.setValue(e.target.value)}
                onBlur={commitExternalNote}
                onKeyDown={handleKeyDown}
                placeholder="External note..."
                className="h-8 text-xs font-light"
                disabled={readonly}
              />
            </div>
          )}
          <div className="grid grid-cols-4 gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Qty</Label>
              <Input
                type="number"
                min={0}
                step={0.5}
                value={amount}
                onChange={(e) =>
                  amountField.setValue(parseFloat(e.target.value) || 0)
                }
                onBlur={commitAmount}
                onKeyDown={handleKeyDown}
                data-testid="line-item-amount"
                {...numberInputProps}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Factor</Label>
              <Input
                type="number"
                min={0}
                step={0.1}
                value={factor}
                onChange={(e) =>
                  factorField.setValue(parseFloat(e.target.value) || 1)
                }
                onBlur={commitFactor}
                onKeyDown={handleKeyDown}
                data-testid="line-item-factor"
                {...numberInputProps}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Price</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={unitPrice}
                onChange={(e) =>
                  unitPriceField.setValue(parseFloat(e.target.value) || 0)
                }
                onBlur={commitUnitPrice}
                onKeyDown={handleKeyDown}
                data-testid="line-item-unit-price"
                {...numberInputProps}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Disc %</Label>
              <Input
                type="number"
                min={0}
                max={100}
                step={1}
                value={discount}
                onChange={(e) =>
                  discountField.setValue(parseFloat(e.target.value) || 0)
                }
                onBlur={commitDiscount}
                onKeyDown={handleKeyDown}
                data-testid="line-item-discount"
                {...numberInputProps}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <span className="text-sm font-medium tabular-nums">
              Total: {formatAmount(total)}
            </span>
          </div>
        </div>

        {notesDialog}
      </>
    );
  }

  // Desktop table row layout
  return (
    <>
      <tr
        ref={ref}
        style={style}
        className={showExternalNote ? "group" : "border-b last:border-0 group"}
      >
        <td className="py-2 pr-1 w-8">
          {dragHandleProps && !readonly ? (
            <GripVertical
              data-testid={`line-item-drag-handle-${item.id}`}
              className="h-4 w-4 text-muted-foreground cursor-grab touch-none opacity-0 group-hover:opacity-100 transition-opacity"
              {...dragHandleProps}
            />
          ) : null}
        </td>
        <td className="py-2 pr-1" style={{ width: 36 }}>
          {typeDropdown}
        </td>
        <td className="py-2 pr-2">{suggestionsPopover}</td>
        <td className="py-2 px-1">
          <Input
            type="number"
            min={0}
            step={0.5}
            value={amount}
            onChange={(e) =>
              amountField.setValue(parseFloat(e.target.value) || 0)
            }
            onBlur={commitAmount}
            onKeyDown={handleKeyDown}
            data-testid="line-item-amount"
            {...numberInputProps}
          />
        </td>
        <td className="py-2 px-1">
          <Input
            type="number"
            min={0}
            step={0.1}
            value={factor}
            onChange={(e) =>
              factorField.setValue(parseFloat(e.target.value) || 1)
            }
            onBlur={commitFactor}
            onKeyDown={handleKeyDown}
            data-testid="line-item-factor"
            {...numberInputProps}
          />
        </td>
        <td className="py-2 px-1">
          <Input
            type="number"
            min={0}
            step={0.01}
            value={unitPrice}
            onChange={(e) =>
              unitPriceField.setValue(parseFloat(e.target.value) || 0)
            }
            onBlur={commitUnitPrice}
            onKeyDown={handleKeyDown}
            data-testid="line-item-unit-price"
            {...numberInputProps}
          />
        </td>
        <td className="py-2 px-1">
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min={0}
              max={100}
              step={1}
              value={discount}
              onChange={(e) =>
                discountField.setValue(parseFloat(e.target.value) || 0)
              }
              onBlur={commitDiscount}
              onKeyDown={handleKeyDown}
              data-testid="line-item-discount"
              {...numberInputProps}
            />
            <span className="text-muted-foreground text-xs">%</span>
          </div>
        </td>
        <td className="py-2 px-1 text-right font-medium tabular-nums">
          {formatAmount(total)}
        </td>
        <td className="py-2 pl-2">
          {actionMenu && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              {actionMenu}
            </div>
          )}
        </td>
      </tr>

      {/* External note row - conditionally shown, spans from name column to end */}
      {showExternalNote && (
        <tr className="border-b last:border-0">
          <td className="pb-2 pr-1"></td>
          <td colSpan={8} className="pb-2">
            <Input
              data-external-note={item.id}
              value={externalNote}
              onChange={(e) => externalNoteField.setValue(e.target.value)}
              onBlur={commitExternalNote}
              onKeyDown={handleKeyDown}
              placeholder="External note (shown on offer)..."
              className="h-7 text-xs font-light"
              disabled={readonly}
            />
          </td>
        </tr>
      )}

      {notesDialog}
    </>
  );
});
